import React from "react";
import { User, AppContext } from "../../../../context/appContext";
import axios from "axios";
import { endpoints } from "../../../../endpoints/endpoints";
import { IUploads } from "../interface/iUploads";
import { enqueueSnackbar } from "notistack";
import { IFile } from "../interface/iFile";

export const ParticipantFilesContext = React.createContext<any>({});

const ParticipantFilesProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const loadFiles = React.useRef(false);
  const [uploads, setUploads] = React.useState<IUploads>({
    editableFile: false,
    files: {
      uploadParticiopant: [],
      uploadCoach: [],
    },
  });

  const fetchFiles = React.useCallback((): void => {
    window.scrollTo(0, 0);

    loadFiles.current = true;

    axios
      .post(endpoints.participant.getFiles, {
        sessionId: user.sessionId,
      })
      .then((response) => {
        checkSession(response);
        setUploads(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [checkSession, user.sessionId]);

  const deleteFile = React.useCallback(
    (
      fileId: string,
      setConfirmDelete: React.Dispatch<React.SetStateAction<boolean>>
    ): void => {
      axios
        .post(endpoints.participant.deleteFile, {
          fileId: fileId,
          sessionId: user.sessionId,
        })
        .then(() => {
          enqueueSnackbar("Die Datei wurde gelöscht!", {
            variant: "success",
          });
          setConfirmDelete(false);

          // Remove the file from the uploads state
          setUploads((prevUploads: IUploads) => {
            const updatedFiles = {
              ...prevUploads.files,
              uploadParticiopant: prevUploads.files.uploadParticiopant.filter(
                (file: IFile) => file.id !== fileId
              ),
              uploadCoach: prevUploads.files.uploadCoach.filter(
                (file: IFile) => file.id !== fileId
              ),
            };
            return { ...prevUploads, files: updatedFiles };
          });
        })
        .catch(() => {
          enqueueSnackbar("Die Datei konnte nicht gelöscht werden!", {
            variant: "error",
          });
        });
    },
    [user.sessionId]
  );

  const downloadFile = React.useCallback(
    (file: IFile): void => {
      axios
        .post(
          endpoints.participant.downloadFile,
          {
            fileId: file.id,
            sessionId: user.sessionId,
          },
          {
            responseType: "blob",
          }
        )
        .then((response) => {
          if (response.data.size === 0) {
            enqueueSnackbar("Die Datei konnte nicht heruntergeladen werden!", {
              variant: "error",
            });
            return;
          }

          // simulate click to download file
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", file.name);
          document.body.appendChild(link);
          link.click();
        })
        .catch(() => {
          enqueueSnackbar("Die Datei konnte nicht heruntergeladen werden!", {
            variant: "error",
          });
        });
    },
    [user.sessionId]
  );

  React.useEffect(() => {
    if (loadFiles.current) return;
    fetchFiles();
  }, [checkSession, fetchFiles, user.sessionId]);

  const value: {} = React.useMemo(
    () => ({
      loadFiles,
      uploads,
      setUploads,
      deleteFile,
      downloadFile,
    }),
    [deleteFile, downloadFile, uploads]
  );

  return (
    <ParticipantFilesContext.Provider value={value}>
      {children}
    </ParticipantFilesContext.Provider>
  );
};

export default ParticipantFilesProvider;
